import { useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ChevronDown, X } from "lucide-react";

/**
 * <RangeSlider label="Opportunity" min={0} max={100} value={[60, 100]} onChange={fn} />
 *
 * Trigger button shows "Opp 60-100" when active, "Opportunity" otherwise.
 * Inside the popover, dual-thumb slider + Clear button.
 */
export function RangeSlider({
  label,
  min = 0,
  max = 100,
  step = 1,
  value, // [lo, hi] OR null when unset
  onChange,
  suffix = "",
  testid,
}) {
  const [open, setOpen] = useState(false);
  const active = Array.isArray(value);
  const [lo, hi] = active ? value : [min, max];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={testid}
          variant="outline"
          className="h-9 justify-between text-xs font-medium text-slate-700 hover:bg-slate-50 min-w-[140px]"
        >
          <span className="truncate">
            <span className="text-slate-500">{label}</span>
            {active ? (
              <span className="ml-1.5 text-slate-900 font-semibold">
                {lo}{suffix}–{hi}{suffix}
              </span>
            ) : (
              <span className="ml-1 text-slate-400">any</span>
            )}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">{label}</div>
        <Slider
          min={min} max={max} step={step}
          value={[lo, hi]}
          onValueChange={(v) => onChange(v)}
          className="my-3"
        />
        <div className="flex items-center justify-between text-xs text-slate-700 mt-2">
          <span className="font-mono">{lo}{suffix}</span>
          <span className="font-mono">{hi}{suffix}</span>
        </div>
        {active && (
          <div className="border-t border-slate-200 mt-3 pt-2 flex items-center justify-end">
            <button
              type="button"
              onClick={() => { onChange(null); setOpen(false); }}
              className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid={testid ? `${testid}-clear` : undefined}
            >
              <X className="h-3 w-3" /> Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
